import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { User } from 'src/app/core/models/user.model';
import { StorageService } from 'src/app/core/services/storage.service';
import { UserService } from 'src/app/core/services/user.service';
import { EditProfileSuccessChangeComponent } from '../../dialog/edit-profile-success-change/edit-profile-success-change.component';
import { ErrorModalComponent } from 'src/app/shared/components/simple/error-modal/error-modal.component';

import { TranslateService } from '@ngx-translate/core';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import { Subject, takeUntil, tap } from 'rxjs';
import { Pii } from 'src/app/core/models/pii.model';
import {
  Question,
  QuestionType,
} from 'src/app/core/models/questionnaire.model';
import { PiiService } from 'src/app/core/services/pii.service';
import { v4 as uuid } from 'uuid';

@Component({
  selector: 'app-edit-profile',
  templateUrl: './edit-profile.component.html',
  styleUrls: ['./edit-profile.component.scss'],
})
export class EditProfileComponent implements OnInit, OnDestroy {
  user: User | null = null;
  phoneNumberCountryPrefix = '+55';

  // Form validation properties
  isFormValid = false;
  formErrors: { [key: string]: boolean } = {};

  languageOptions = [
    { value: 'PT', text: 'Português (BR)' },
    { value: 'EN', text: 'English' },
  ];

  @ViewChild('phoneinput') phoneInput!: NgxMatIntlTelInputComponent;

  addressQuestion: Question = {
    configuration: {},
    created_at: new Date(),
    id: '',
    name: 'Address',
    questionnaire_id: '',
    response_type: QuestionType.ADDRESS,
    slug: '',
    statement: '',
  };

  pii?: Pii;

  profileImg: string | null = null;
  profileName = '-';
  profileLoading = false;
  isUploading = false;
  // TODO: Implement the user data

  name: string = '';

  language = 'EN';

  streetNumber = '';
  address = '';
  complement = '';
  city = '';
  zipcode = '';
  countryState = '';

  preferredCountries = ['gb', 'us'];

  formControls = {
    first_name: new FormControl('', [Validators.required]),
    middle_name: new FormControl(''),
    last_name: new FormControl('', [Validators.required]),
  };

  isSaving = false;

  private destroySubject = new Subject<void>();

  constructor(
    public dialog: MatDialog,
    private us: UserService,
    private ss: StorageService,
    private piis: PiiService,
    private snackbar: MatSnackBar,
    private translateService: TranslateService
  ) {
    console.log('Normal edit profile');
    this._initValidation();
  }

  private _initValidation() {
    this.createErrorSubscription('first_name');
    this.createErrorSubscription('middle_name');
    this.createErrorSubscription('last_name');
  }

  private createErrorSubscription(
    formControlName: 'first_name' | 'middle_name' | 'last_name'
  ) {
    this.formControls[formControlName]?.valueChanges
      .pipe(
        tap(() => {
          this.formErrors[formControlName] =
            !!this.formControls[formControlName].errors?.['required'];
          // Recompute overall validity when any control changes
          this.validateFields();
        }),
        takeUntil(this.destroySubject)
      )
      .subscribe();
  }

  private validateFields(): void {
    this.isFormValid =
      !this.formErrors['first_name'] &&
      !this.formErrors['middle_name'] &&
      !this.formErrors['last_name'];
  }

  /** Returns array of human friendly missing required fields */
  getMissingRequiredFields(): string[] {
    const missing: string[] = [];
    if (this.formControls['first_name'].invalid) missing.push(this.translateService.instant('EditProfileFirstName'));
    if (this.formControls['middle_name'].invalid) missing.push(this.translateService.instant('EditProfileMiddleName'));
    if (this.formControls['last_name'].invalid) missing.push(this.translateService.instant('EditProfileLastName'));
    return missing;
  }

  /** Returns a tooltip string listing missing fields (or empty) */
  getMissingFieldsTooltip(): string {
    const missing = this.getMissingRequiredFields();
    if (missing.length === 0) return '';
    return this.translateService.instant('MissingFields') + ': ' + missing.join(', ');
  }

  async ngOnInit(): Promise<void> {
    const user = await this.us.getCurrentUser();
    this.user = user;
    this.buildProfile();
    console.log('user', user);

    if (!user || !user.primary_email) {
      console.error('No user loaded', user);
      return;
    }

    this.language = user.language || 'EN';

    const pii = await this.piis.getFromUserId(user.id!);
    this.pii = pii;
    if (!this.pii.attributes) {
      const displayName = user.display_name || '';
      const nameParts = displayName.split(' ').filter(Boolean); // Filter removes empty strings
      const fname = nameParts[0] || '';
      const lname = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
      const middleNames =
        nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : ''; // Joins all middle names

      this.pii.attributes = {
        first_name: fname,
        last_name: lname,
        middle_name: middleNames,
      };
    }

    if (!this.pii.contacts) {
      console.log('[PROFILE] Did not have contacts', this.pii);
      this.pii.contacts = {
        phone: '',
        email: user.primary_email,
      };
    }

    console.log('this.user', this.user);
    console.log('this.pii', this.pii);

    // Update form with user data
    this.updateFormWithUserData();
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  private updateFormWithUserData(): void {
    if (this.pii && this.pii.attributes) {
      // Patch reactive form controls and keep ngModel bindings in sync by
      // setting the control values. The template also binds directly to pii.attributes.
      this.formControls['first_name'].patchValue(this.pii.attributes.first_name || '');
      this.formControls['middle_name'].patchValue(this.pii.attributes.middle_name || '');
      this.formControls['last_name'].patchValue(this.pii.attributes.last_name || '');

      // Validate initial state
      this.formErrors['first_name'] = !!this.formControls['first_name'].errors?.['required'];
      this.formErrors['middle_name'] = !!this.formControls['middle_name'].errors?.['required'];
      this.formErrors['last_name'] = !!this.formControls['last_name'].errors?.['required'];
      this.validateFields();
    }
  }

  changePhone(ev: any) {
    console.log('[PROFILE] changePhone', { ev, pii: this.pii });
  }

  // Called from template when name fields change to keep reactive form in sync
  onNameChange(field: 'first_name' | 'middle_name' | 'last_name', value: any) {
    // Update reactive form control without emitting a cycle back to ngModel
    this.formControls[field].setValue(value || '', { emitEvent: true });
    // Update error map immediately
    this.formErrors[field] = !!this.formControls[field].errors?.['required'];
    this.validateFields();
  }

  buildProfile() {
    this.us.getCurrentUser().then((u) => {
      // Ckech user is logged

      console.log('User', u);

      if (u && u.avatar_url) {
        const imgUrl = `profile-pictures/${u.id}/${u.avatar_url}`;
        const bucket = 'profile-pictures';
        const path = `${u.id}/${u.avatar_url}`;

        console.log('Image URL', imgUrl);

        console.log('Bucket', bucket);
        console.log('Path', path);
        this.ss
          .signedUrl(bucket, path)
          .then((url) => {
            console.log('URL', url);
            if (url.error) {
              console.error('Error getting URL', url.error);
              return;
            }

            if (!url.data) {
              console.error('Error getting URL', url);
              return;
            }

            if (!url.data.signedUrl) {
              console.error('Error getting URL', url);
              return;
            }

            console.log('Signed URL', url.data.signedUrl);
            this.profileImg = url.data.signedUrl;

          })
          .catch((err) => {
            console.error('Error getting URL', err);
          });

        return;
      }

      this.profileImg = null;
      if (!u || !u.display_name) {
        this.profileName = '-';
      } else {
        // Get the First Letter by each word splitted by space and limit to 2.
        // Always get the the first and the last letter
        // Example: John Doe Mark => JM

        if (!u) {
          this.profileName = '-';
          return;
        }

        if (!u.display_name) {
          this.profileName = '-';
          return;
        }

        if (!u.display_name.trim()) {
          this.profileName = '-';
          return;
        }
        if (u.display_name === 'undefined') {
          this.profileName = '-';
          return;
        }

        const nameSplits = u.display_name.split(' ');
        const firstName = nameSplits[0];
        const lastName =
          nameSplits.length > 1 ? nameSplits[nameSplits.length - 1] : null;

        const firstLetter = firstName[0];
        const lastLetter = lastName ? lastName[0] : '';

        this.profileName = `${firstLetter}${lastLetter}`;
      }
      console.log('Profile Text', this.profileName);
    });
  }

  async changeProfileImg() {
    try {
      if (this.profileImg) {
        // TODO: Remove Image form bucket
        const bucket = 'profile-pictures';
        const path = `${this.user!.id}/${this.user!.avatar_url}`;

        this.isUploading = true;
        try {
          await this.ss.DeleteFilesInBucket(bucket, [path]);
          await this.us.updateUser({ ...this.user, avatar_url: null, pii: undefined } as any);
          this.buildProfile();
          this.snackbar.open(
            this.translateService.instant('ProfileImageDeleteSuccess'),
            this.translateService.instant('Close'),
            { duration: 3000 }
          );
        } catch (error) {
          console.error('Error deleting profile image', error);
          this.snackbar.open(
            this.translateService.instant('ProfileImageDeleteFailed'),
            this.translateService.instant('Close'),
            { duration: 5000 }
          );
        } finally {
          this.isUploading = false;
        }
      } else {
        // Pick new image
        const pickFile = document.createElement('input');
        pickFile.type = 'file';
        pickFile.accept = 'image/png,image/jpeg,application/pdf';
        pickFile.click();

        pickFile.onchange = async () => {
          const file = pickFile.files?.[0];
          if (!file) return;

          console.log('File', file);

          // Validate file type
          const allowedTypes = [
            'image/jpeg',
            'image/jpg',
            'image/png',
            'application/pdf',
          ];
          if (!allowedTypes.includes(file.type)) {
            this.snackbar.open(
              this.translateService.instant('FileTypeNotAllowed'),
              this.translateService.instant('Close'),
              { duration: 5000 }
            );
            return;
          }

          // Validate file size (2MB = 2 * 1024 * 1024 bytes)
          const maxSize = 2 * 1024 * 1024; // 2MB
          if (file.size > maxSize) {
            this.snackbar.open(
              this.translateService.instant('FileTooBig'),
              this.translateService.instant('Close'),
              { duration: 5000 }
            );
            return;
          }

          this.isUploading = true;
          this.snackbar.open(
            this.translateService.instant('ProfileImageUploading'),
            this.translateService.instant('Close'),
            { duration: 3000 }
          );

          try {
            const ext = file.name.split('.').pop();
            // Save on storage
            const bucket = 'profile-pictures';
            const imageID = `${uuid()}.${ext}`;

            console.log('Bucket', bucket);
            console.log('Image ID', imageID);
            const path = `${this.user!.id}/${imageID}`;

            const { data: img, error } = await this.ss.upload(
              bucket,
              path,
              file
            );

            if (error) {
              console.error('Error saving image', error);
              this.snackbar.open(
                this.translateService.instant('UploadFailed'),
                this.translateService.instant('Close'),
                { duration: 5000 }
              );
              return;
            }

            // Save Path on user
            await this.us.updateUser({ ...this.user, pii: undefined, avatar_url: imageID });
            this.buildProfile();
            this.snackbar.open(
              this.translateService.instant('ProfileImageUploadSuccess'),
              this.translateService.instant('Close'),
              { duration: 3000 }
            );
          } catch (error) {
            console.error('Error uploading profile image', error);
            this.snackbar.open(
              this.translateService.instant('UploadFailed'),
              this.translateService.instant('Close'),
              { duration: 5000 }
            );
          } finally {
            this.isUploading = false;
          }
        };
      }
    } catch (error) {
      console.error('Error changing profile image', error);
      this.snackbar.open(
        this.translateService.instant('UploadFailed'),
        this.translateService.instant('Close'),
        { duration: 5000 }
      );
      this.isUploading = false;
    }
  }

  async handleSubmit() {
    // Validate form before submission
    this.validateFields();

    if (!this.isFormValid) {
      const missing = this.getMissingRequiredFields();
      const message = missing.length
        ? `${this.translateService.instant('PleaseCompleteForm')}: ${missing.join(', ')}`
        : this.translateService.instant('PleaseCompleteForm');
      this.snackbar.open(message, this.translateService.instant('Close'), { duration: 7000 });
      this.openErrorDialog();
      return;
    }

    if (!this.pii) {
      throw new Error('[PROFILE] PII MISSING');
    }

    try {
      this.isSaving = true;
      // Optionally show a snackbar indicating saving started
      this.snackbar.open(this.translateService.instant('Saving'), undefined, { duration: 1500 });
      // Update PII with form values
      const formValues = {
        first_name: this.formControls['first_name'].getRawValue(),
        middle_name: this.formControls['middle_name'].getRawValue(),
        last_name: this.formControls['last_name'].getRawValue(),
      };

      if (this.pii.attributes) {
        this.pii.attributes.first_name = formValues.first_name!;
        this.pii.attributes.middle_name = formValues.middle_name!;
        this.pii.attributes.last_name = formValues.last_name!;
      }

      const dn = `${this.pii.attributes.first_name || ''} ${
        this.pii.attributes.last_name || ''
      }`
        .trim()
        .replace(/\s{2,}/g, ' ');

      const nuser: Partial<User> = {
        ...this.user,
        display_name: dn,
        language: this.language,
      };

      const ln = `${this.pii.attributes.first_name || ''} ${
        this.pii.attributes.middle_name || ''
      } ${this.pii.attributes.last_name || ''}`.trim();
      const npii: Partial<Pii> = {
        ...this.pii,
        legal_name: ln.trim().replace(/\s{2,}/g, ' '),
      };

      delete npii.search_vector;
      delete npii.search_vector2;
      console.log('[PROFILE] Pre save', { nuser, npii });

      console.log('saving nuser', nuser);
      const res = await this.us.updateUser({...nuser, pii: undefined});
      const rpii = await this.piis.update(npii);
      this.openDialog();
    } catch (error) {
      console.error(error);
      this.openErrorDialog();
    } finally {
      // Ensure spinner is cleared and button re-enabled
      this.isSaving = false;
    }
  }

  changeLang(newValue: string) {
    console.log(
      '[Edit profile] Change lang from',
      this.language,
      'to',
      newValue
    );
    if (newValue != this.language) {
      this.language = newValue;
      this.translateService.use(this.language.toLowerCase());
    }
  }

  private openDialog() {
    const ref = this.dialog.open(EditProfileSuccessChangeComponent);
    return ref;
  }

  private openErrorDialog() {
    const ref = this.dialog.open(ErrorModalComponent, {
      data: {
        title: 'Error when saving',
        message: 'Something went wrong and your changes were not saved. Try again.'
      }
    });
    ref.componentInstance.title = 'Error when saving';
    ref.componentInstance.message = 'Something went wrong and your changes were not saved. Try again.';
    return ref;
  }
}
